import { TOOL_DISPLAY_NAMES } from '../../utils/toolConstants';

// Shared label helpers for the Energy Hub's drill views.

/** `doc_code_worker` → `Doc Code Worker`. The last-resort readable form. */
export const humanize = (value) =>
    String(value || '')
        .replace(/[_-]+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * A human label for a BE `pipeline_step`.
 *
 * Resolution order: the user's dictionary (so a translated deployment wins) →
 * `TOOL_DISPLAY_NAMES`, the FE's existing single source of truth for
 * step→display (it already backs the chat trace chips, so a new BE step gets a
 * label here the moment it is registered there) → humanized fallback, which
 * means an unregistered step degrades to something readable instead of a blank
 * cell.
 */
export const stepLabel = (t, step) =>
    t(`energyStep_${step}`, TOOL_DISPLAY_NAMES[step] || humanize(step));

/** Categories are BE-defined strings, so the dictionary is opt-in per key. */
export const categoryLabel = (t, category) =>
    t(`energyCategory_${category}`, humanize(category));

/** Pillars are a closed set of five. */
export const pillarLabel = (t, pillar) =>
    t(`energyPillar_${pillar}`, humanize(pillar));

// #530: channels hidden in the web app (their BE key is still in the data).
const HIDDEN_CHANNELS = new Set(['whatsapp']);

/**
 * The ORIGIN label for a charge's source channel (Labelling energy #315).
 *
 * `channel` is the raw BE value ('telegram_convo'); `fallback` is the English
 * label the BE already computed for it. The dictionary wins so a translated
 * deployment reads in its own language, and the BE's label is the fallback so a
 * channel added server-side is readable here BEFORE anyone adds a key —
 * exactly the resolution order `stepLabel` uses.
 */
export const channelLabel = (t, channel, fallback) => {
    // #530: a channel hidden in the web app is never named — a historic charge
    // from it reads as generic "Messaging", whatever label the BE ships.
    if (HIDDEN_CHANNELS.has(String(channel || '').toLowerCase())) {
        return t('energyChannelOtherMessaging', 'Messaging');
    }
    return t(`energyChannel_${channel}`, fallback || humanize(channel));
};
